/**
 * DecisionRecord persistence — localStorage backend.
 *
 * v1 ships localStorage-only because Dan's `/services/decisions/`
 * endpoint isn't deployed yet. When it lands, this module gains a
 * server adapter and the localStorage path stays as the offline /
 * pre-server fallback (same pattern as gibbs-client-rag).
 *
 * Storage shape: a single localStorage key `gibbs.decisionRecords`
 * holds an array of records. Read/write the full array on each
 * operation — v1 corpus size is tiny (~tens of records per user
 * over many months). v2 may switch to IndexedDB when scale matters.
 *
 * Tests inject an in-memory storage adapter via `setStorageAdapter`
 * so Node tests don't need jsdom.
 *
 * @module gibbs/decision-records/storage
 */

import { validateDecisionRecord, createDecisionRecord } from './schema';
import { signRecord } from './signature';

const STORAGE_KEY = 'gibbs.decisionRecords';

/**
 * Storage adapter shape — anything implementing this interface can
 * back the store. Default uses globalThis.localStorage; tests inject
 * an in-memory Map-based adapter.
 *
 * @typedef {Object} StorageAdapter
 * @property {(key: string) => string | null} getItem
 * @property {(key: string, value: string) => void} setItem
 * @property {(key: string) => void} removeItem
 */

let adapter = null;

/** @returns {StorageAdapter} */
function getAdapter() {
    if (adapter) return adapter;
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
        return globalThis.localStorage;
    }
    // No localStorage available — fall back to in-memory (test path).
    adapter = createInMemoryAdapter();
    return adapter;
}

function createInMemoryAdapter() {
    const store = new Map();
    return {
        getItem(key) { return store.has(key) ? store.get(key) : null; },
        setItem(key, value) { store.set(key, String(value)); },
        removeItem(key) { store.delete(key); },
    };
}

/**
 * Inject a custom storage adapter. Test-only.
 *
 * @param {StorageAdapter|null} a - Pass null to reset to default.
 */
export function setStorageAdapter(a) {
    adapter = a;
}

/**
 * Reset the in-memory cache (test only).
 */
export function resetStorageForTest() {
    adapter = null;
}

function readAllRecords() {
    const raw = getAdapter().getItem(STORAGE_KEY);
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[decision-records] storage JSON parse failed:', err?.message);
        return [];
    }
}

function writeAllRecords(records) {
    getAdapter().setItem(STORAGE_KEY, JSON.stringify(records));
}

/**
 * Persist a decision record. Idempotent on `id`: saving a record
 * with the same id replaces the prior entry (NOT a duplicate write).
 * Adds a SHA-256 signature over the canonical record body before
 * writing.
 *
 * @param {import('./schema').DecisionRecord | Partial<...>} input
 *   Either a complete record or a partial that gets defaults via
 *   `createDecisionRecord`.
 * @returns {Promise<{
 *   record: import('./schema').DecisionRecord,
 *   error: string | null,
 *   persistedLocally: boolean,
 * }>}
 */
export async function saveDecisionRecord(input) {
    const draft = (input && input.schemaVersion)
        ? input
        : createDecisionRecord(input || {});

    const validation = validateDecisionRecord(draft);
    if (!validation.valid) {
        return {
            record: draft,
            error: `validation_failed: ${validation.errors.join('; ')}`,
            persistedLocally: false,
        };
    }

    const signature = await signRecord(draft);
    const finalRecord = { ...draft, signature };

    const records = readAllRecords();
    const existingIdx = records.findIndex((r) => r.id === finalRecord.id);
    if (existingIdx >= 0) {
        records[existingIdx] = finalRecord;
    } else {
        records.push(finalRecord);
    }
    writeAllRecords(records);

    return {
        record: finalRecord,
        error: null,
        persistedLocally: true,
    };
}

/**
 * List decision records matching the given filters. All filters are
 * optional and AND together.
 *
 * @param {{
 *   reviewerId?: string,
 *   since?: string,
 *   target?: string,
 * }} [filters]
 * @returns {Array<import('./schema').DecisionRecord>}
 */
export function listDecisionRecords(filters = {}) {
    const records = readAllRecords();
    return records.filter((r) => {
        if (filters.reviewerId
            && r.reviewer?.id !== filters.reviewerId) return false;
        if (filters.since && r.createdAt) {
            if (Date.parse(r.createdAt) < Date.parse(filters.since)) return false;
        }
        if (filters.target) {
            const matchesPdb = r.workspace?.proteinPdbId === filters.target;
            const matchesProject = r.workspace?.projectName === filters.target;
            if (!matchesPdb && !matchesProject) return false;
        }
        return true;
    }).sort((a, b) => {
        // Newest first
        const ta = Date.parse(a.createdAt) || 0;
        const tb = Date.parse(b.createdAt) || 0;
        return tb - ta;
    });
}

/**
 * Fetch a single record by id.
 *
 * @param {string} id
 * @returns {import('./schema').DecisionRecord | null}
 */
export function getDecisionRecord(id) {
    if (!id) return null;
    const records = readAllRecords();
    return records.find((r) => r.id === id) || null;
}

/**
 * Soft-delete a decision record. v1 actually removes from storage;
 * v2 with server backend will tombstone instead so audit chains
 * remain intact.
 *
 * @param {string} id
 * @returns {boolean} true if a record was removed
 */
export function deleteDecisionRecord(id) {
    if (!id) return false;
    const records = readAllRecords();
    const filtered = records.filter((r) => r.id !== id);
    if (filtered.length === records.length) return false;
    writeAllRecords(filtered);
    return true;
}
